import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { msalConfig } from "./authConfig";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./components/LoginPage";
import ChatUI from "./components/ChatUI";

const msalInstance = new PublicClientApplication(msalConfig);

const ProtectedRoute = ({ children }) => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="mx-auto mb-4"
            style={{
              width: "48px",
              height: "48px",
              animation: "spin 1s linear infinite",
              transformOrigin: "center",
            }}
          >
            <svg viewBox="0 0 50 50" width="48" height="48">
              <defs>
                <linearGradient
                  id="spinner-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#2563eb" />
                  <stop offset="100%" stopColor="#9b5cf6" />
                </linearGradient>
              </defs>
              <path
                fill="none"
                stroke="url(#spinner-gradient)"
                strokeWidth="4"
                strokeLinecap="round"
                d="M25,5 A20,20 0 0,1 45,25"
              />
            </svg>
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
    </>
  );
};

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // ✅ Clear stale MSAL cache that might cause issues
        try {
          const accounts = msalInstance.getAllAccounts();
          console.log(`Found ${accounts.length} cached account(s)`);

          // Clear any stale interaction flags
          Object.keys(localStorage).forEach((key) => {
            if (key.includes("msal") && key.includes("interaction")) {
              const value = localStorage.getItem(key);
              if (value === "interaction_in_progress") {
                console.log("Clearing stale MSAL interaction flag:", key);
                localStorage.removeItem(key);
              }
            }
          });

          // Clear any stale session storage flags
          Object.keys(sessionStorage).forEach((key) => {
            if (key.includes("msal") && key.includes("interaction")) {
              console.log("Clearing stale MSAL session flag:", key);
              sessionStorage.removeItem(key);
            }
          });
        } catch (cacheError) {
          console.warn("Cache cleanup error:", cacheError);
        }

        // ✅ Safari-specific: Handle redirect with better error handling
        try {
          const response = await msalInstance.handleRedirectPromise();
          if (response) {
            console.log("Redirect handled successfully:", response);
            // Clear any stale login attempt flags
            try {
              sessionStorage.removeItem("login_attempt_time");
            } catch (e) {}
          }
        } catch (redirectError) {
          console.error("Redirect handling error:", redirectError);

          // If there's a critical redirect error, clear the cache and reload
          if (
            redirectError.errorCode === "hash_not_deserialized" ||
            redirectError.errorCode === "state_mismatch"
          ) {
            console.error(
              "Critical redirect error detected, clearing cache...",
            );
            try {
              await msalInstance.clearCache();
              localStorage.clear();
              sessionStorage.clear();
            } catch (e) {}
            // Reload page to start fresh
            window.location.reload();
            return;
          }

          // Don't block initialization on non-critical redirect errors
          if (redirectError.errorCode !== "interaction_in_progress") {
            console.error("Non-interaction redirect error:", redirectError);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("MSAL initialization failed:", error);
        setIsInitialized(true);
      }
    };

    initializeMsal();
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div
            className="mx-auto mb-4"
            style={{
              width: "48px",
              height: "48px",
              animation: "spin 1s linear infinite",
            }}
          >
            <svg viewBox="0 0 50 50" width="48" height="48">
              <defs>
                <linearGradient
                  id="init-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#2563eb" />
                  <stop offset="100%" stopColor="#9b5cf6" />
                </linearGradient>
              </defs>
              <path
                fill="none"
                stroke="url(#init-gradient)"
                strokeWidth="4"
                strokeLinecap="round"
                d="M25,5 A20,20 0 0,1 45,25"
              />
            </svg>
          </div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ChatUI />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <ChatUI />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
